import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus, 
  FileText,
  Download,
  Trash2, 
  Scissors, 
  Package, 
  Columns,
  GanttChart,
  CheckCircle,
  Clock,
  AlertTriangle,
  Calendar
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { useAppStore } from '../store';
import { Production, Sample, ProductionTask, SampleTask } from '../types';
import { format, addWeeks, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isWithinInterval, parseISO } from 'date-fns';
import KanbanView from '../components/ui/KanbanView';
import GanttView from '../components/ui/GanttView';
import ProductionTaskModal from '../components/modals/ProductionTaskModal';
import SampleTaskModal from '../components/modals/SampleTaskModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import ErrorModal from '../components/modals/ErrorModal';
import TimelineView from '../components/ui/TimelineView';
import { formatCurrency } from '../utils/formatters';

type EntityType = 'production' | 'sample';
type ViewMode = 'calendar' | 'timeline' | 'kanban' | 'gantt';

const ProductionManager: React.FC = () => {
  const { 
    productions, 
    samples, 
    productionTasks, 
    sampleTasks, 
    suppliers, 
    clients,
    addProductionTask, 
    updateProductionTask, 
    deleteProductionTask,
    addSampleTask,
    updateSampleTask,
    deleteSampleTask
  } = useAppStore();
  
  // State for entity selection
  const [entityType, setEntityType] = useState<EntityType>('production');
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // State for task management
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ProductionTask | SampleTask | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  
  // State for view mode
  const [viewMode, setViewMode] = useState<ViewMode | 'supplier'>('calendar');
  
  // Estado para los filtros de la pestaña de proveedor
  const [supplierFilter, setSupplierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('month');
  // Estado para mostrar solo tareas atrasadas
  const [showOnlyLate, setShowOnlyLate] = useState(false);
  // Estado para filtrar por tipo de entidad
  const [entityTypeFilter, setEntityTypeFilter] = useState<'all' | 'production' | 'sample'>('all');

  // Calcular fechas de inicio y fin según el filtro de período
  const now = new Date();
  let periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
  let periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  if (periodFilter === '7days') {
    periodStart = new Date(now);
    periodStart.setDate(now.getDate() - 7);
    periodEnd = now;
  } else if (periodFilter === '30days') {
    periodStart = new Date(now);
    periodStart.setDate(now.getDate() - 30);
    periodEnd = now;
  } else if (periodFilter === 'quarter') {
    periodStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    periodEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
  } else if (periodFilter === 'year') {
    periodStart = new Date(now.getFullYear(), 0, 1);
    periodEnd = new Date(now.getFullYear(), 11, 31);
  }

  // Filtrar tareas por proveedor, estado y período
  const filteredSupplierTasks = [...productionTasks, ...sampleTasks].filter(task => {
    const taskStart = task.start_date ? new Date(task.start_date) : null;
    const taskEnd = task.due_date ? new Date(task.due_date) : null;
    const inPeriod = (!taskStart || (taskStart >= periodStart && taskStart <= periodEnd)) ||
                     (!taskEnd || (taskEnd >= periodStart && taskEnd <= periodEnd));
    const supplierOk = !supplierFilter || task.supplier_id === supplierFilter;
    const statusOk = !statusFilter || task.status === statusFilter;
    
    // Filtrar por tipo de entidad
    const isProductionTask = !!task.production_id;
    const isSampleTask = !!task.sample_id;
    const entityTypeOk = entityTypeFilter === 'all' || 
                        (entityTypeFilter === 'production' && isProductionTask) ||
                        (entityTypeFilter === 'sample' && isSampleTask);
    
    // Filtrar tareas de producciones/muestras terminadas
    const production = productions.find(p => p.id === task.production_id);
    const sample = samples.find(s => s.id === task.sample_id);
    const entity = production || sample;
    
    // Si no se encuentra la entidad, excluir la tarea
    if (!entity) {
      return false;
    }
    
    // Verificar que la entidad no esté terminada
    const entityNotFinished = production 
      ? production.status !== 'finished'
      : sample.status !== 'finished' && sample.status !== 'shipped';
    
    return inPeriod && supplierOk && statusOk && entityTypeOk && entityNotFinished;
  });
  
  // Get the selected entity
  const selectedEntity = useMemo<Production | Sample | null>(() => {
    if (!selectedEntityId) return null;
    
    if (entityType === 'production') {
      return productions.find(p => p.id === selectedEntityId) || null;
    } else {
      return samples.find(s => s.id === selectedEntityId) || null;
    }
  }, [selectedEntityId, entityType, productions, samples]);
  
  // Get tasks for the selected entity
  const entityTasks = useMemo(() => {
    if (!selectedEntityId) return [];
    
    if (entityType === 'production') {
      return productionTasks.filter(task => task.production_id === selectedEntityId);
    } else {
      return sampleTasks.filter(task => task.sample_id === selectedEntityId);
    }
  }, [selectedEntityId, entityType, productionTasks, sampleTasks]);
  
  // Filter out completed/shipped entities
  const activeEntities = useMemo(() => {
    if (entityType === 'production') {  
      return productions.filter(p => p.status !== 'finished');
    } else {
      return samples.filter(s => s.status !== 'finished' && s.status !== 'shipped');
    }
  }, [entityType, productions, samples]);
  
  // Get all active productions and samples for calendar view
  const calendarEntities = useMemo(() => {
    const activeProductions = productions.filter(p => p.status !== 'finished');
    const activeSamples = samples.filter(s => s.status !== 'finished' && s.status !== 'shipped');
    
    return [
      ...activeProductions.map(p => ({
        id: p.id,
        type: 'production' as const,
        name: p.name,
        client_id: p.client_id,
        delivery_date: p.delivery_date,
        status: p.status
      })),
      ...activeSamples.map(s => ({
        id: s.id,
        type: 'sample' as const,
        name: s.name,
        client_id: s.client_id,
        delivery_date: s.delivery_date,
        status: s.status
      }))
    ];
  }, [productions, samples]);
  
  // Get client name for the selected entity
  const getClientName = (clientId: string) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Cliente no encontrado';
  };
  
  // Get supplier name for a task
  const getSupplierName = (supplierId: string | null) => {
    if (!supplierId) return 'Sin proveedor';
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier ? supplier.name : 'Proveedor no encontrado';
  };
  
  // Handle task status change (for Kanban drag and drop)
  const handleTaskStatusChange = async (taskId: string, newStatus: 'pending' | 'in_progress' | 'finished') => {
    try {
      if (entityType === 'production') {
        const task = productionTasks.find(t => t.id === taskId);
        if (task) {
          await updateProductionTask(taskId, { status: newStatus });
        }
      } else {
        const task = sampleTasks.find(t => t.id === taskId);
        if (task) {
          await updateSampleTask(taskId, { status: newStatus });
        }
      }
    } catch (error: any) {
      console.error('Error updating task status:', error);
      setError(error.message || 'Error al actualizar el estado de la tarea');
      setShowErrorModal(true);
    }
  };
  
  // Calculate total cost of tasks
  const totalTasksCost = useMemo(() => {
    return entityTasks.reduce((sum, task) => sum + task.cost, 0);
  }, [entityTasks]);
  
  // Calculate task status counts
  const taskStatusCounts = useMemo(() => {
    return {
      pending: entityTasks.filter(task => task.status === 'pending').length,
      inProgress: entityTasks.filter(task => task.status === 'in_progress').length,
      finished: entityTasks.filter(task => task.status === 'finished').length,
    };
  }, [entityTasks]);
  
  // Filter entities based on search query
  const filteredEntities = useMemo(() => {
    const entities = activeEntities;
    
    if (!searchQuery) return entities;
    
    return entities.filter(entity => {
      const clientName = getClientName(entity.client_id);
      return (
        entity.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        clientName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }, [activeEntities, searchQuery]);
  
  // Handle entity type change
  const handleEntityTypeChange = (type: EntityType) => {
    setEntityType(type);
    setSelectedEntityId(null);
    setSearchQuery('');
  };
  
  // Handle entity selection
  const handleEntitySelect = (id: string) => {
    setSelectedEntityId(id);
  };
  
  // Get file links for the selected entity
  const getFileLinks = () => {
    if (!selectedEntity) return [];
    
    const files = [];
    
    if (selectedEntity.approved_file) {
      files.push({
        name: 'Ficha Aprobada',
        url: selectedEntity.approved_file
      });
    }
    
    if (selectedEntity.dtf_file) {
      files.push({
        name: 'Archivo DTF',
        url: selectedEntity.dtf_file
      });
    }
    
    if (entityType === 'production' && (selectedEntity as Production).embroidery_file) {
      files.push({
        name: 'Archivo Bordado',
        url: (selectedEntity as Production).embroidery_file
      });
    }
    
    if (entityType === 'sample' && (selectedEntity as Sample).shipping_label) {
      files.push({
        name: 'Etiqueta de Envío',
        url: (selectedEntity as Sample).shipping_label
      });
    }
    
    return files;
  };
  
  // Handle adding a new task
  const handleAddTask = () => {
    setSelectedTask(null);
    setIsTaskModalOpen(true);
  };
  
  // Handle editing a task
  const handleEditTask = (task: ProductionTask | SampleTask) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);
  };
  
  // Handle deleting a task
  const handleDeleteTask = (id: string) => {
    setTaskToDelete(id);
    setIsDeleteModalOpen(true);
  };
  
  // Confirm task deletion
  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    
    try {
      if (entityType === 'production') {
        await deleteProductionTask(taskToDelete);
      } else {
        await deleteSampleTask(taskToDelete);
      }
    } catch (error: any) {
      console.error('Error deleting task:', error);
      setError(error.message || 'Error al eliminar la tarea');
      setShowErrorModal(true);
    }
  };
  
  // Save a task
  const handleSaveTask = async (task: Omit<ProductionTask | SampleTask, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      if (entityType === 'production') {
        if (selectedTask) {
          await updateProductionTask(selectedTask.id, task as Omit<ProductionTask, 'id' | 'created_at' | 'updated_at'>);
        } else {
          await addProductionTask(task as Omit<ProductionTask, 'id' | 'created_at' | 'updated_at'>);
        }
      } else {
        if (selectedTask) {
          await updateSampleTask(selectedTask.id, task as Omit<SampleTask, 'id' | 'created_at' | 'updated_at'>);
        } else {
          await addSampleTask(task as Omit<SampleTask, 'id' | 'created_at' | 'updated_at'>);
        }
      }
      setIsTaskModalOpen(false);
    } catch (error: any) {
      console.error('Error saving task:', error);
      setError(error.message || 'Error al guardar la tarea');
      setShowErrorModal(true);
    }
  };
  
  // Format entity options for dropdown
  const entityOptions = useMemo(() => {
    return [
      { value: '', label: `Seleccionar ${entityType === 'production' ? 'producción' : 'muestra'}...` },
      ...filteredEntities.map(entity => {
        const clientName = getClientName(entity.client_id);
        return {
          value: entity.id,
          label: `${entity.name} - ${clientName}`
        };
      })
    ];
  }, [filteredEntities, entityType]);
  
  // Calendar view functions
  const getCalendarDays = () => {
    const today = new Date();
    const startDate = startOfWeek(today, { weekStartsOn: 1 }); // Start from Monday
    const endDate = endOfWeek(addWeeks(startDate, 3), { weekStartsOn: 1 }); // 4 weeks total
    
    return eachDayOfInterval({ start: startDate, end: endDate });
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'; 
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200';
    }
  };
  
  // Get type color based on entity type (production or sample)
  const getTypeColor = (type: 'production' | 'sample') => {
    return type === 'production' 
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200';
  };
  
  // Get type icon based on entity type
  const getTypeIcon = (type: 'production' | 'sample') => {
    return type === 'production' 
      ? <Package className="h-3 w-3 mr-1" />
      : <Scissors className="h-3 w-3 mr-1" />;
  };
  
  const calendarDays = useMemo(() => getCalendarDays(), []);
  
  // Render Calendar View
  const renderCalendarView = () => {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-7 gap-1">
          {/* Day headers */}
          {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map((day, index) => (
            <div key={index} className="text-center font-medium text-gray-700 dark:text-gray-300 p-2">
              {day}
            </div>
          ))}
          
          {/* Calendar days */}
          {calendarDays.map((day, index) => {
            const isToday = isSameDay(day, new Date());
            const dayEntities = calendarEntities.filter(entity => 
              isSameDay(parseISO(entity.delivery_date), day)
            );
            
            return (
              <div 
                key={index} 
                className={`min-h-[120px] border border-gray-200 dark:border-gray-700 rounded-md p-2 ${
                  isToday ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800' : ''
                }`}
              >
                <div className="text-right text-sm font-medium mb-1">
                  <span className={isToday ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400'}>
                    {format(day, 'dd')}
                  </span>
                </div>
                
                <div className="space-y-1 overflow-y-auto max-h-[80px]">
                  {dayEntities.map(entity => (
                    <div 
                      key={entity.id} 
                      className={`text-xs p-1 rounded ${getTypeColor(entity.type)} cursor-pointer hover:opacity-80`}
                      onClick={() => {
                        setEntityType(entity.type);
                        setSelectedEntityId(entity.id);
                        setViewMode('timeline');
                      }}
                    >
                      <div className="flex items-center">
                        {getTypeIcon(entity.type)}
                        <span className="truncate">{entity.name}</span>
                      </div>
                      <div className="text-xs opacity-80 truncate">
                        {getClientName(entity.client_id)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
  return (
    <div className="space-y-6">
<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
    Gestión de {entityType === 'production' ? 'Producción' : 'Muestras'}
  </h2>
  <div className="flex flex-wrap gap-2 w-full sm:w-auto">
    <Button
      variant={viewMode === 'calendar' ? 'primary' : 'outline'}
      onClick={() => setViewMode('calendar')}
      icon={<Calendar className="h-4 w-4" />}
      className="flex-1 sm:flex-none text-sm"
    >
      <span className="hidden sm:inline">Calendario</span>
      <span className="sm:hidden"></span>
    </Button>
    <Button
      variant={viewMode === 'timeline' ? 'primary' : 'outline'}
      onClick={() => setViewMode('timeline')}
      icon={<Clock className="h-4 w-4" />}
      className="flex-1 sm:flex-none text-sm"
    >
      <span className="hidden sm:inline">Tareas</span>
      <span className="sm:hidden"></span>
    </Button>
    <Button
      variant={viewMode === 'kanban' ? 'primary' : 'outline'}
      onClick={() => setViewMode('kanban')}
      icon={<Columns className="h-4 w-4" />}
      className="flex-1 sm:flex-none text-sm"
    >
      <span className="hidden sm:inline">Kanban</span>
      <span className="sm:hidden"></span>
    </Button>
    <Button
      variant={viewMode === 'gantt' ? 'primary' : 'outline'}
      onClick={() => setViewMode('gantt')}
      icon={<GanttChart className="h-4 w-4" />}
      className="flex-1 sm:flex-none text-sm"
    >
      <span className="hidden sm:inline">Gantt</span>
      <span className="sm:hidden"></span>
    </Button>
    <Button
      variant={viewMode === 'supplier' ? 'primary' : 'outline'}
      onClick={() => setViewMode('supplier')}
      icon={<Package className="h-4 w-4" />}
      className="flex-1 sm:flex-none text-sm"
    >
      <span className="hidden sm:inline">Tareas por Proveedor</span>
      <span className="sm:hidden"></span>
    </Button>
  </div>
</div>

{/* Entity Selection Wizard - Only show if not in calendar view and no es supplier */}
{viewMode !== 'calendar' && viewMode !== 'supplier' && (
  <Card>
    <CardContent>
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex gap-2">
          <Button
            variant={entityType === 'production' ? 'primary' : 'outline'}
            onClick={() => handleEntityTypeChange('production')}
            icon={<Package className="h-4 w-4" />}
          >
            Producciones
          </Button>
          <Button
            variant={entityType === 'sample' ? 'primary' : 'outline'}
            onClick={() => handleEntityTypeChange('sample')}
            icon={<Scissors className="h-4 w-4" />}
          >
            Muestras
          </Button>
        </div>
        <div className="flex-1 w-full">
          <div className="relative">
            <Select
              label=""
              value={selectedEntityId || ''}
              onChange={handleEntitySelect}
              options={entityOptions}
              searchable={true}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
)}

{/* Calendar View */}
{viewMode === 'calendar' && (
  <Card>
    <CardHeader title="Calendario de Entregas" />
    <CardContent>
      {renderCalendarView()}
    </CardContent>
  </Card>
)}
      
      {/* Nueva pestaña: Tareas por Proveedor */}
      {viewMode === 'supplier' && (
        <Card>
          <CardHeader title="Tareas por Proveedor" />
          <CardContent>
            {/* Filtros por proveedor, estado y período */}
            <div className="flex flex-wrap gap-3 mb-4 items-center">
              <div className="w-40">
                <Select
                  label="Proveedor"
                  value={supplierFilter}
                  onChange={setSupplierFilter}
                  options={[
                    { value: '', label: 'Todos' },
                    ...suppliers.map(s => ({ value: s.id, label: s.name }))
                  ]}
                />
              </div>
              <div className="w-40">
                <Select
                  label="Estado"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    { value: '', label: 'Todos' },
                    { value: 'pending', label: 'No comenzado' },
                    { value: 'in_progress', label: 'En Proceso' },
                    { value: 'finished', label: 'Terminado' },
                  ]}
                />
              </div>
              <div className="w-40">
                <Select
                  label="Período"
                  value={periodFilter}
                  onChange={setPeriodFilter}
                  options={[
                    { value: 'month', label: 'Este mes' },
                    { value: '7days', label: '7 días' },
                    { value: '30days', label: '30 días' },
                    { value: 'quarter', label: 'Trimestre' },
                    { value: 'year', label: 'Este año' },
                  ]}
                />
              </div>
              <div className="w-40">
                <Select
                  label="Tipo"
                  value={entityTypeFilter}
                  onChange={setEntityTypeFilter}
                  options={[
                    { value: 'all', label: 'Ver todo' },
                    { value: 'production', label: 'Producciones' },
                    { value: 'sample', label: 'Muestras' },
                  ]}
                />
              </div>
              <Button
                variant={showOnlyLate ? 'primary' : 'outline'}
                className="h-10 px-4"
                onClick={() => setShowOnlyLate(v => !v)}
              >
                <svg className="w-4 h-4 mr-2 text-red-700 dark:text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {showOnlyLate ? 'Ver todas' : 'Ver solo atrasadas'}
              </Button>
              <Button
                variant="outline"
                className="h-10 px-4"
                onClick={() => {
                  setSupplierFilter('');
                  setStatusFilter('');
                  setPeriodFilter('month');
                  setEntityTypeFilter('all');
                  setShowOnlyLate(false);
                }}
              >
                Limpiar Filtros
              </Button>
            </div>
            {/* Agrupación de tareas por proveedor */}
            <div className="space-y-6">
              {suppliers.map(supplier => {
                let tasks = filteredSupplierTasks.filter(task => task.supplier_id === supplier.id);
                if (showOnlyLate) {
                  tasks = tasks.filter(t => t.end_date && new Date(t.end_date) < now && t.status !== 'finished');
                }
                if (tasks.length === 0) return null;
                // Tareas atrasadas: fecha de fin menor a hoy y no terminadas
                const atrasadas = tasks.filter(t => t.end_date && new Date(t.end_date) < now && t.status !== 'finished').length;
                return (
                  <div key={supplier.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Package className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                        <span className="text-lg font-semibold text-gray-900 dark:text-white">{supplier.name}</span>
                      </div>
                      <div className="flex flex-wrap gap-4 mt-2 md:mt-0">
                        <span className="text-sm text-gray-500">Tareas: <span className="font-bold text-gray-900 dark:text-white">{tasks.length}</span></span>
                        <span className="text-sm text-gray-500">Atrasadas: <span className={`font-bold ${atrasadas > 0 ? 'text-red-600' : 'text-green-600'}`}>{atrasadas}</span></span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tarea</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Producción/Muestra</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estado</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fecha de inicio</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fecha de fin</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                          {tasks.map(task => {
                            const production = productions.find(p => p.id === task.production_id);
                            const sample = samples.find(s => s.id === task.sample_id);
                            const entity = production || sample;
                            const isAtrasada = task.end_date && new Date(task.end_date) < now && task.status !== 'finished';
                            return (
                              <tr key={task.id} className={isAtrasada ? 'bg-red-50 dark:bg-red-900/20' : ''}>
                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                  <span 
                                    className="cursor-pointer hover:underline hover:text-blue-600 dark:hover:text-blue-400"
                                    onClick={() => {
                                      // Determinar si es tarea de producción o muestra
                                      const taskEntityType = task.production_id ? 'production' : 'sample';
                                      const taskEntityId = task.production_id || task.sample_id;
                                      
                                      // Configurar el contexto para el modal
                                      setEntityType(taskEntityType);
                                      setSelectedEntityId(taskEntityId);
                                      setSelectedTask(task);
                                      setIsTaskModalOpen(true);
                                    }}
                                  >
                                    {task.task_name || '-'}
                                  </span>
                                  {isAtrasada && (
                                    <span className="ml-2 inline-flex items-center text-xs font-bold text-red-700 dark:text-red-400 animate-pulse">
                                      <svg className="w-4 h-4 mr-1 text-red-700 dark:text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                      ¡ATRASADA!
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                                  <span className="cursor-pointer hover:underline" onClick={() => {
                                    const entityType = task.production_id ? 'production' : 'sample';
                                    setEntityType(entityType);
                                    setSelectedEntityId(entity?.id || null);
                                    setViewMode('timeline');
                                  }}>
                                    {entity?.name || '-'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  <select
                                    className={`px-2 py-1 rounded-full text-xs font-semibold border-0 focus:ring-2 focus:ring-blue-500 ${
                                      task.status === 'pending' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' :
                                      task.status === 'in_progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200' :
                                      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                                    }`}
                                    value={task.status}
                                    onChange={async (e) => {
                                      const newStatus = e.target.value as 'pending' | 'in_progress' | 'finished';
                                      try {
                                        if (task.production_id) {
                                          await updateProductionTask(task.id, { status: newStatus });
                                        } else {
                                          await updateSampleTask(task.id, { status: newStatus });
                                        }
                                      } catch (error: any) {
                                        console.error('Error updating task status:', error);
                                        setError(error.message || 'Error al actualizar el estado de la tarea');
                                        setShowErrorModal(true);
                                      }
                                    }}
                                  >
                                    <option value="pending">No comenzado</option>
                                    <option value="in_progress">En Proceso</option>
                                    <option value="finished">Terminado</option>
                                  </select>
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                  <input
                                    type="date"
                                    className="bg-transparent border-0 p-0 text-sm focus:ring-2 focus:ring-blue-500 rounded"
                                    value={task.start_date ? task.start_date.split('T')[0] : ''}
                                    onChange={async (e) => {
                                      const newDate = e.target.value;
                                      try {
                                        if (task.production_id) {
                                          await updateProductionTask(task.id, { start_date: newDate });
                                        } else {
                                          await updateSampleTask(task.id, { start_date: newDate });
                                        }
                                      } catch (error: any) {
                                        console.error('Error updating task start date:', error);
                                        setError(error.message || 'Error al actualizar la fecha de inicio');
                                        setShowErrorModal(true);
                                      }
                                    }}
                                  />
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                  <input
                                    type="date"
                                    className="bg-transparent border-0 p-0 text-sm focus:ring-2 focus:ring-blue-500 rounded"
                                    value={task.end_date ? task.end_date.split('T')[0] : ''}
                                    onChange={async (e) => {
                                      const newDate = e.target.value;
                                      try {
                                        if (task.production_id) {
                                          await updateProductionTask(task.id, { end_date: newDate });
                                        } else {
                                          await updateSampleTask(task.id, { end_date: newDate });
                                        }
                                      } catch (error: any) {
                                        console.error('Error updating task end date:', error);
                                        setError(error.message || 'Error al actualizar la fecha de fin');
                                        setShowErrorModal(true);
                                      }
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
              {/* Si no hay tareas para ningún proveedor */}
              {suppliers.every(supplier => filteredSupplierTasks.filter(task => task.supplier_id === supplier.id).length === 0) && (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  No hay tareas asignadas a proveedores en este período.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Selected Entity Details */}
      {selectedEntity && viewMode !== 'calendar' && viewMode !== 'supplier' && (
        <Card>
          <CardHeader title={`Detalles de ${entityType === 'production' ? 'Producción' : 'Muestra'}`} />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
<div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
  <div className="flex justify-between items-start mb-2">
    <h3 className="text-lg font-medium text-gray-900 dark:text-white">{selectedEntity.name}</h3>
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
      Math.round((taskStatusCounts.finished / (taskStatusCounts.pending + taskStatusCounts.inProgress + taskStatusCounts.finished || 1)) * 100) >= 75 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200' :
      Math.round((taskStatusCounts.finished / (taskStatusCounts.pending + taskStatusCounts.inProgress + taskStatusCounts.finished || 1)) * 100) >= 50 ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200' :
      Math.round((taskStatusCounts.finished / (taskStatusCounts.pending + taskStatusCounts.inProgress + taskStatusCounts.finished || 1)) * 100) >= 25 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' :
      'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
    }`}>
      {Math.round((taskStatusCounts.finished / (taskStatusCounts.pending + taskStatusCounts.inProgress + taskStatusCounts.finished || 1)) * 100)}%
    </span>
  </div>
  <p className="text-sm text-gray-600 dark:text-gray-400">
    Cliente: {getClientName(selectedEntity.client_id)}
  </p>
  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
    Fecha de entrega: <span className={`font-bold ${
      (() => {
        const deliveryDate = new Date(selectedEntity.delivery_date);
        const today = new Date();
        const diffTime = deliveryDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0 || diffDays <= 4) return 'text-red-600 dark:text-red-400';
        if (diffDays <= 7) return 'text-amber-600 dark:text-amber-400';
        return 'text-gray-900 dark:text-white';
      })()
    }`}>
      {format(new Date(selectedEntity.delivery_date), 'dd/MM/yyyy')}
      {(() => {
        const deliveryDate = new Date(selectedEntity.delivery_date);
        const today = new Date();
        const diffTime = deliveryDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) return ' (Vencida)';
        if (diffDays <= 4) return ` (${diffDays} días)`;
        if (diffDays <= 7) return ` (${diffDays} días)`;
        return '';
      })()}
    </span>
  </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Vendedor: {selectedEntity.salesperson}
                </p>
                {selectedEntity.comment && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    {selectedEntity.comment}
                  </p>
                )}
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Archivos Adjuntos</h3>
                {getFileLinks().length > 0 ? (
                  <div className="space-y-2">
                    {getFileLinks().map((file, index) => (
                      <a 
                        key={index}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                      >
                        <FileText className="h-4 w-4 text-indigo-600 dark:text-indigo-400 mr-2" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">{file.name}</span>
                        <Download className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No hay archivos adjuntos</p>
                )}
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Estado de Tareas</h3>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      <span className="text-lg font-semibold text-amber-600 dark:text-amber-400">{taskStatusCounts.pending}</span>
                    </div>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">No comenzadas</p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      <span className="text-lg font-semibold text-blue-600 dark:text-blue-400">{taskStatusCounts.inProgress}</span>
                    </div>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">En Proceso</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <span className="text-lg font-semibold text-green-600 dark:text-green-400">{taskStatusCounts.finished}</span>
                    </div>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">Terminadas</p>
                  </div>
                </div>
              </div>
            </div>
            
            {viewMode !== 'supplier' && (
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Tareas ({entityTasks.length})
                </h3>
                <Button
                  variant="primary"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={handleAddTask}
                >
                  Agregar Tarea
                </Button>
              </div>
            )}
            
            {viewMode === 'timeline' ? (
              <TimelineView
                tasks={entityTasks as ProductionTask[]}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                getSupplierName={getSupplierName}
              />
            ) : viewMode === 'kanban' ? (
              <KanbanView
                tasks={entityTasks as ProductionTask[]}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                getSupplierName={getSupplierName}
                onStatusChange={handleTaskStatusChange}
              />
            ) : viewMode === 'gantt' ? (
              <GanttView
                tasks={entityTasks as ProductionTask[]}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                getSupplierName={getSupplierName}
              />
            ) : null}
          </CardContent>
        </Card>
      )}
      
      {!selectedEntity && viewMode !== 'calendar' && viewMode !== 'supplier' && (
        <></>
      )}
      
      {/* Task Modal */}
      {selectedEntityId && (
        entityType === 'production' ? (
          <ProductionTaskModal
            isOpen={isTaskModalOpen}
            onClose={() => {
              setIsTaskModalOpen(false);
              setSelectedTask(null);
            }}
            onSave={handleSaveTask}
            productionId={selectedEntityId}
            initialData={selectedTask as ProductionTask}
          />
        ) : (
          <SampleTaskModal
            isOpen={isTaskModalOpen}
            onClose={() => {
              setIsTaskModalOpen(false);
              setSelectedTask(null);
            }}
            onSave={handleSaveTask}
            sampleId={selectedEntityId}
            initialData={selectedTask as SampleTask}
          />
        )
      )}
      
      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setTaskToDelete(null);
        }}
        onConfirm={confirmDeleteTask}
        title="Eliminar Tarea"
        message="¿Estás seguro de que deseas eliminar esta tarea? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />
      
      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />
    </div>
  );
};

export default ProductionManager;